import Layout from "@/components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock,
  Send,
  Facebook,
  Instagram,
  Twitter,
  Crown
} from "lucide-react";
import contactHeroBg from "@/assets/contact-hero-bg.jpg";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import GoldenParticles from "@/components/GoldenParticles";
import WaveGoldDivider from "@/components/WaveGoldDivider";
import VIPBadge from "@/components/VIPBadge";
import AnimatedSection from "@/components/AnimatedSection";

const Contact = () => {
  const { getConfig } = useSiteConfig();
  
  const contactInfo = [
    {
      icon: Phone,
      title: "VIP Hotline",
      details: [getConfig('contact_phone', '+1 (809) 123-4567')],
      description: "Available 24/7 for VIP bookings and concierge service"
    },
    {
      icon: Mail,
      title: "Email Address",
      details: [getConfig('contact_email', 'info@tropunca.com')],
      description: "We respond within 1 hour for VIP inquiries"
    },
    {
      icon: MapPin,
      title: "Location",
      details: [getConfig('contact_location', 'Punta Cana')],
      description: "Serving all of Punta Cana and surrounding areas"
    },
    {
      icon: Clock,
      title: "VIP Hours",
      details: ["24/7 Service Available"],
      description: "Emergency and airport transfers available anytime"
    }
  ];

  const serviceAreas = [
    "Punta Cana", "Bavaro", "Cap Cana", "Uvero Alto",
    "La Romana", "Santo Domingo", "Samaná", "Puerto Plata"
  ];

  const socialLinks = [
    { icon: Facebook, name: "Facebook", url: getConfig('social_facebook')?.url || '#', enabled: getConfig('social_facebook')?.enabled || false },
    { icon: Instagram, name: "Instagram", url: getConfig('social_instagram')?.url || '#', enabled: getConfig('social_instagram')?.enabled || false },
    { icon: Twitter, name: "TikTok", url: getConfig('social_tiktok')?.url || '#', enabled: getConfig('social_tiktok')?.enabled || false }
  ].filter(social => social.enabled);

  return (
    <Layout>
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center justify-center text-white bg-cover bg-center bg-no-repeat overflow-hidden"
        style={{ backgroundImage: `url(${contactHeroBg})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/90 via-black/70 to-primary/30"></div>
        <GoldenParticles count={20} />
        
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-4xl mx-auto text-center">
            <VIPBadge variant="exclusive" size="lg" icon="crown" className="mb-6" animated>
              VIP Concierge
            </VIPBadge>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              <span className="text-gradient-gold">Let's Connect</span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              Ready to experience the best of Dominican Republic? Contact TROPUNCA VIP for premium transportation and unforgettable exclusive tours.
            </p>
          </AnimatedSection>
        </div>
        
        <WaveGoldDivider className="absolute bottom-0 left-0 right-0" />
      </section>

      {/* Contact Information */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <VIPBadge variant="gold" className="mb-4">Contact Information</VIPBadge>
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-gold">
              We're Here to Help
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the most convenient way to contact our VIP concierge team. We're committed to responding quickly and providing all the information you need.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <Card className="text-center hover:shadow-gold transition-all duration-300 hover:-translate-y-2 border-transparent hover:border-primary/30">
                  <CardHeader>
                    <div className="mx-auto w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mb-4">
                      <info.icon className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{info.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 mb-4">
                      {info.details.map((detail, detailIndex) => (
                        <div key={detailIndex} className="font-medium text-primary">
                          {detail}
                        </div>
                      ))}
                    </div>
                    <CardDescription className="text-base">
                      {info.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection className="text-center mb-12">
              <VIPBadge variant="gold" className="mb-4">Send Us a Message</VIPBadge>
              <h2 className="text-4xl font-bold mb-6 text-gradient-gold">
                VIP Contact Form
              </h2>
              <p className="text-lg text-muted-foreground">
                Fill out the form below and our VIP concierge will get back to you within 1 hour
              </p>
            </AnimatedSection>

            <AnimatedSection>
              <Card className="border-primary/20 hover:border-primary/40 transition-all shadow-gold">
                <CardHeader>
                  <CardTitle className="text-2xl text-center text-gradient-gold">Book or Inquire</CardTitle>
                  <CardDescription className="text-center text-base">
                    Whether you want to book a VIP service or just have questions, we're here to help
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input id="firstName" placeholder="Your first name" required className="border-primary/30 focus:border-primary" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input id="lastName" placeholder="Your last name" required className="border-primary/30 focus:border-primary" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input id="email" type="email" placeholder="your.email@example.com" required className="border-primary/30 focus:border-primary" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number</Label>
                        <Input id="phone" placeholder="+1 (xxx) xxx-xxxx" className="border-primary/30 focus:border-primary" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="service">VIP Service Type *</Label>
                        <select 
                          id="service" 
                          className="w-full px-3 py-2 border border-primary/30 rounded-md bg-background text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                          required
                        >
                          <option value="">Select a VIP service</option>
                          <option value="airport-transfer">VIP Airport Transfer</option>
                          <option value="private-tour">Private Exclusive Tour</option>
                          <option value="group-transport">Executive Group Transportation</option>
                          <option value="custom-package">Custom VIP Package</option>
                          <option value="general-inquiry">General VIP Inquiry</option>
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="date">Preferred Date</Label>
                        <Input id="date" type="date" className="border-primary/30 focus:border-primary" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="guests">Number of VIP Guests</Label>
                      <select 
                        id="guests" 
                        className="w-full px-3 py-2 border border-primary/30 rounded-md bg-background text-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                      >
                        <option value="">Select number of guests</option>
                        <option value="1">1 VIP Guest</option>
                        <option value="2">2 VIP Guests</option>
                        <option value="3-4">3-4 VIP Guests</option>
                        <option value="5-8">5-8 VIP Guests</option>
                        <option value="9-15">9-15 VIP Guests</option>
                        <option value="16+">16+ VIP Guests</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message *</Label>
                      <Textarea 
                        id="message" 
                        placeholder="Please provide details about your VIP request, including pickup location, destination, special requirements, or any questions you have..."
                        rows={5}
                        required
                        className="border-primary/30 focus:border-primary"
                      />
                    </div>

                    <Button className="w-full bg-gradient-gold text-white hover:opacity-90 shadow-gold text-lg py-6 group">
                      <Send className="mr-2 h-5 w-5" />
                      Send VIP Request
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <AnimatedSection className="text-center mb-16">
            <VIPBadge variant="gold" className="mb-4">Service Areas</VIPBadge>
            <h2 className="text-4xl font-bold mb-6 text-gradient-gold">
              Where We Operate
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We provide VIP services throughout the Dominican Republic, with special focus on the most popular tourist destinations
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
            {serviceAreas.map((area, index) => (
              <AnimatedSection key={index} delay={index * 50}>
                <Card className="text-center p-4 hover:shadow-gold transition-all duration-300 hover:-translate-y-1 border-transparent hover:border-primary/30">
                  <div className="text-primary font-medium">{area}</div>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="py-24 bg-vip-dark relative overflow-hidden">
        <GoldenParticles count={20} />
        <div className="container mx-auto px-4 relative z-10">
          <AnimatedSection className="max-w-4xl mx-auto">
            <Card className="border-primary/30 bg-white/5 backdrop-blur-sm">
              <CardHeader className="text-center">
                <div className="mx-auto w-20 h-20 bg-gradient-gold rounded-full flex items-center justify-center mb-4 animate-pulse-glow">
                  <Phone className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-3xl mb-4 text-white">24/7 VIP Hotline</CardTitle>
                <CardDescription className="text-white/80 text-lg">
                  For urgent VIP assistance, emergency transportation, or last-minute bookings
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <div className="text-5xl font-bold mb-4 text-gradient-gold">{getConfig('contact_phone', '+1 (809) 123-4567')}</div>
                <p className="text-white/80 mb-6">
                  Available 24 hours a day, 7 days a week for VIP emergency situations, airport transfers, and urgent transportation needs
                </p>
                <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold text-lg px-8 py-6">
                  <Phone className="mr-2 h-5 w-5" />
                  Call VIP Hotline Now
                </Button>
              </CardContent>
            </Card>
          </AnimatedSection>
        </div>
      </section>

      {/* Social Media */}
      {socialLinks.length > 0 && (
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <AnimatedSection className="text-center mb-12">
              <VIPBadge variant="gold" className="mb-4">Follow Us</VIPBadge>
              <h2 className="text-4xl font-bold mb-6 text-gradient-gold">
                Stay Connected
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Follow us on social media for VIP travel tips, destination highlights, and exclusive offers
              </p>
            </AnimatedSection>

            <div className="flex justify-center space-x-6">
              {socialLinks.map((social, index) => (
                <a 
                  key={index}
                  href={social.url}
                  className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center text-white hover:scale-110 hover:shadow-gold transition-all duration-300"
                >
                  <social.icon className="h-8 w-8" />
                </a>
              ))}
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
};

export default Contact;
